import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Separator } from "../ui/separator";
import { 
  Mail, 
  Phone, 
  Globe, 
  MapPin, 
  Clock,
  Send,
  CheckCircle,
  Linkedin,
  Twitter,
  Youtube,
  MessageCircle,
  Users,
  ArrowRight
} from "lucide-react";

export function Contact() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    designation: "",
    helpType: "",
    message: ""
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock form submission
    console.log("Form submitted:", formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        company: "",
        designation: "",
        helpType: "",
        message: ""
      });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "kumarjit.mukherjee@nexprocureai.com",
      href: "mailto:kumarjit.mukherjee@nexprocureai.com"
    },
    {
      icon: Phone,
      label: "Phone",
      value: "+91 98308 46904",
      href: "tel:+919830846904"
    },
    {
      icon: Globe,
      label: "Website",
      value: "www.nexprocureai.com",
      href: "https://www.nexprocureai.com"
    }
  ];

  const socialLinks = [
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: "#",
      color: "bg-blue-600 hover:bg-blue-700"
    },
    {
      name: "Twitter",
      icon: Twitter,
      href: "#",
      color: "bg-blue-400 hover:bg-blue-500"
    },
    {
      name: "YouTube",
      icon: Youtube,
      href: "#",
      color: "bg-red-600 hover:bg-red-700"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Contact Form Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Drop Us a Line</h2>
              <p className="text-lg text-muted-foreground">
                Fill out the form below and we'll get back to you within 24 hours
              </p>
            </div>

            <Card className="shadow-lg">
              <CardContent className="p-8">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Full Name */}
                      <div className="space-y-2">
                        <Label htmlFor="fullName">Full Name *</Label>
                        <Input
                          id="fullName"
                          type="text"
                          value={formData.fullName}
                          onChange={(e) => handleInputChange("fullName", e.target.value)}
                          required
                          placeholder="Enter your full name"
                          className="bg-input-background"
                        />
                      </div>

                      {/* Work Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email">Work Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          required
                          placeholder="Enter your work email"
                          className="bg-input-background"
                        />
                      </div>

                      {/* Phone Number */}
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          placeholder="Enter your phone number"
                          className="bg-input-background"
                        />
                      </div>

                      {/* Company Name */}
                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name</Label>
                        <Input
                          id="company"
                          type="text"
                          value={formData.company}
                          onChange={(e) => handleInputChange("company", e.target.value)}
                          placeholder="Enter your company name"
                          className="bg-input-background"
                        />
                      </div>

                      {/* Designation/Role */}
                      <div className="space-y-2">
                        <Label htmlFor="designation">Designation/Role</Label>
                        <Input
                          id="designation"
                          type="text"
                          value={formData.designation}
                          onChange={(e) => handleInputChange("designation", e.target.value)}
                          placeholder="Enter your job title"
                          className="bg-input-background"
                        />
                      </div>

                      {/* How can we help you? */}
                      <div className="space-y-2">
                        <Label htmlFor="helpType">How can we help you?</Label>
                        <Select value={formData.helpType} onValueChange={(value) => handleInputChange("helpType", value)}>
                          <SelectTrigger className="bg-input-background">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="demo">Request a Demo</SelectItem>
                            <SelectItem value="partner">Partner Inquiry</SelectItem>
                            <SelectItem value="support">Support</SelectItem>
                            <SelectItem value="general">General Question</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Your Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message">Your Message</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange("message", e.target.value)}
                        placeholder="Tell us more about your needs..."
                        rows={6}
                        className="bg-input-background"
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="text-center">
                      <Button type="submit" size="lg" className="group">
                        <Send className="mr-2 h-4 w-4" />
                        Submit
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-12">
                    <div className="flex items-center justify-center mb-6">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                    </div>
                    <h3 className="text-2xl mb-4">Thanks for reaching out!</h3>
                    <p className="text-lg text-muted-foreground">
                      Our team will contact you within 24 hours.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Direct Contact Info Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Prefer to Reach Us Directly?</h2>
              <p className="text-lg text-muted-foreground">
                Get in touch using any of these direct contact methods
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Information */}
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
                          <info.icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold mb-1">{info.label}</h3>
                          <a 
                            href={info.href}
                            className="text-muted-foreground hover:text-primary transition-colors"
                          >
                            {info.value}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Office Address */}
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-xl">Head Office</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <address className="not-italic text-muted-foreground mb-4">
                    MODULE NO: 3, Gokul Baral Street,<br />
                    2nd floor, Kolkata - 700012<br />
                    India
                  </address>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Office Hours: Mon - Fri, 9:00 AM - 6:00 PM IST</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-4 text-3xl lg:text-4xl font-bold">Stay Connected</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Follow us on social media for the latest updates, insights, and industry news
            </p>

            <div className="flex justify-center gap-6">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className={`inline-flex items-center justify-center h-12 w-12 rounded-full text-white transition-colors ${social.color}`}
                  aria-label={`Follow us on ${social.name}`}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>


          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-3xl lg:text-4xl text-white font-bold">
              Ready to Transform Your Procurement?
            </h2>
            <p className="mb-8 text-lg text-white/90">
              Join hundreds of companies who trust NexProcureAI to streamline their procurement processes and drive business growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group bg-white text-primary hover:bg-gray-100">
                <MessageCircle className="mr-2 h-4 w-4" />
                Schedule a Demo
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" className="bg-white text-primary border-white hover:bg-gray-100">
                <Users className="mr-2 h-4 w-4" />
                Partner with Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}